import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leaveBalanceService } from '../services/leaveBalanceService';
import { leaveTypeService } from '../services/leaveTypeService';
import { userService } from '../services/userService';
import { LeaveBalance, GrantLeaveBalanceData } from '../types/leaveBalance';
import { LeaveType } from '../types/leaveType';
import { User } from '../types/user';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';

type SortField = 'user_name' | 'leave_type_name' | 'balance' | 'valid_until';
type SortOrder = 'asc' | 'desc';

const LeaveBalancesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employment, setEmployment] = useState<Employment>('active');
  // 初回ロードと在職状態の effect が同時に走ると同じ API を2回叩くため、初回だけ後者を止める。
  const skipFirstEmployment = useRef(true);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [sortField, setSortField] = useState<SortField>('user_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [grantFormData, setGrantFormData] = useState<GrantLeaveBalanceData>({
    user_id: 0,
    leave_type_id: 0,
    days: 0,
    hours: 0,
    valid_from: '',
    valid_until: '',
    reason: '',
  });

  // フィルター状態
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<string>('all');
  const [selectedHireMonth, setSelectedHireMonth] = useState<string>('all');

  // 付与モーダル用のフィルター・選択状態
  const [grantModalDepartmentId, setGrantModalDepartmentId] = useState<string>('all');
  const [grantModalSearchQuery, setGrantModalSearchQuery] = useState('');
  const [grantModalHireMonth, setGrantModalHireMonth] = useState<string>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  // 在職状態を変えたら**従業員一覧だけ**取り直す。
  // 残数の表は users で絞って描いているので、残数そのものを取り直す必要はない。
  useEffect(() => {
    // 初回は上の初回ロードが同じ内容を取るので、ここでは走らせない（二重取得の防止）。
    if (skipFirstEmployment.current) {
      skipFirstEmployment.current = false;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const usersData = await userService.getUsers(employment);
        if (!cancelled) setUsers(usersData);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || '従業員一覧の取得に失敗しました');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employment]);


  const loadData = async () => {
    try {
      setLoading(true);
      const [balancesData, usersData, leaveTypesData] = await Promise.all([
        leaveBalanceService.getCompanyBalances(),
        userService.getUsers(employment),
        leaveTypeService.getAll(),
      ]);
      setBalances(balancesData);
      setUsers(usersData);
      setLeaveTypes(leaveTypesData.filter((lt) => lt.requires_balance));
      setError(null);
    } catch (err: any) {
      console.error('データ取得エラー:', err);
      setError(err.response?.data?.error || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // 部署でフィルタリングされたユーザー一覧（従業員選択用）
  const filteredUsers = users.filter(u => {
    if (selectedDepartmentId !== 'all' && u.department_id?.toString() !== selectedDepartmentId) {
      return false;
    }
    if (userSearchQuery && !u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
        !u.username.toLowerCase().includes(userSearchQuery.toLowerCase())) {
      return false;
    }
    if (selectedHireMonth !== 'all' && u.hire_date) {
      const hireDate = new Date(u.hire_date);
      const hireMonth = `${hireDate.getFullYear()}-${String(hireDate.getMonth() + 1).padStart(2, '0')}`;
      if (hireMonth !== selectedHireMonth) {
        return false;
      }
    }
    return true;
  });

  // 部署一覧を取得
  const departments = Array.from(new Set(users.map(u => u.department_id).filter(Boolean)))
    .map(deptId => {
      const user = users.find(u => u.department_id === deptId);
      return { id: deptId, name: user?.department_name || '不明' };
    });

  // 入社月一覧を取得（YYYY-MM形式）
  const hireMonths = Array.from(new Set(
    users
      .filter(u => u.hire_date)
      .map(u => {
        const hireDate = new Date(u.hire_date!);
        return `${hireDate.getFullYear()}-${String(hireDate.getMonth() + 1).padStart(2, '0')}`;
      })
  )).sort().reverse(); // 新しい月が先

  const [ledgerDownloading, setLedgerDownloading] = useState(false);

  /**
   * 年次有給休暇管理簿（CSV）をダウンロードする。
   *
   * 労基則24条の7で作成が義務づけられ、3年の保存が必要な帳簿。
   * 2026-08-22 まで休暇データの出口が1つも無く、この帳簿を
   * キンログから取り出せなかったため追加した。
   */
  const handleDownloadLedger = async () => {
    setError(null);
    setLedgerDownloading(true);
    try {
      await leaveBalanceService.downloadAnnualPaidLedger();
    } catch (err: any) {
      console.error('年次有給休暇管理簿の出力に失敗しました:', err);
      setError(err.response?.data?.error || '年次有給休暇管理簿の出力に失敗しました');
    } finally {
      setLedgerDownloading(false);
    }
  };

  // 付与モーダル用のフィルタリングされたユーザー一覧
  const grantModalFilteredUsers = users.filter(u => {
    if (grantModalDepartmentId !== 'all' && u.department_id?.toString() !== grantModalDepartmentId) {
      return false;
    }
    if (grantModalSearchQuery && !u.name.toLowerCase().includes(grantModalSearchQuery.toLowerCase()) &&
        !u.username.toLowerCase().includes(grantModalSearchQuery.toLowerCase())) {
      return false;
    }
    if (grantModalHireMonth !== 'all') {
      if (!u.hire_date) return false;
      const hireMonth = u.hire_date.substring(0, 7); // YYYY-MM
      if (hireMonth !== grantModalHireMonth) return false;
    }
    return true;
  });

  const getFilteredAndSortedBalances = (): LeaveBalance[] => {
    // フィルタリング
    let filtered = [...balances];

    // 従業員フィルター（部署 + 検索を含む）
    const filteredUserIds = new Set(filteredUsers.map(u => u.id));
    filtered = filtered.filter(b => filteredUserIds.has(b.user_id));

    // 休暇種別フィルター
    if (selectedLeaveTypeId !== 'all') {
      filtered = filtered.filter(b => b.leave_type_id?.toString() === selectedLeaveTypeId);
    }

    // ソート
    const sorted = filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'user_name':
          aValue = a.user_name || '';
          bValue = b.user_name || '';
          break;
        case 'leave_type_name':
          aValue = a.leave_type_name || '';
          bValue = b.leave_type_name || '';
          break;
        case 'balance':
          aValue = a.balance_days * 8 + a.balance_remaining_hours;
          bValue = b.balance_days * 8 + b.balance_remaining_hours;
          break;
        case 'valid_until':
          aValue = new Date(a.valid_until).getTime();
          bValue = new Date(b.valid_until).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const handleOpenGrantModal = () => {
    const today = new Date();
    const oneYearLater = new Date();
    oneYearLater.setFullYear(today.getFullYear() + 1);

    setGrantFormData({
      user_id: 0,
      leave_type_id: 0,
      days: 0,
      hours: 0,
      valid_from: today.toISOString().split('T')[0],
      valid_until: oneYearLater.toISOString().split('T')[0],
      reason: '',
    });
    setSelectedUserIds([]);
    setGrantModalDepartmentId('all');
    setGrantModalSearchQuery('');
    setShowGrantModal(true);
  };

  const handleCloseGrantModal = () => {
    setShowGrantModal(false);
    setSelectedUserIds([]);
    setGrantModalDepartmentId('all');
    setGrantModalSearchQuery('');
  };

  const handleGrantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedUserIds.length === 0) {
      setError('対象社員を選択してください');
      return;
    }

    if (!grantFormData.leave_type_id) {
      setError('休暇種別を選択してください');
      return;
    }

    try {
      // 各ユーザーに対して順番に付与
      for (const userId of selectedUserIds) {
        await leaveBalanceService.grant({
          ...grantFormData,
          user_id: userId,
        });
      }

      setSuccessMessage(`${selectedUserIds.length}名に休暇を付与しました`);
      setError(null);
      handleCloseGrantModal();
      loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('休暇付与エラー:', err);
      setError(err.response?.data?.error || '休暇の付与に失敗しました');
      setSuccessMessage(null);
    }
  };

  const handleRevoke = async (balanceId: number, userName: string, leaveTypeName: string) => {
    if (!window.confirm(`${userName}さんの${leaveTypeName}を取り消しますか？\n※既に消費されている場合、残数がマイナスになります。`)) {
      return;
    }

    const reason = window.prompt('取り消し理由を入力してください（任意）:');

    try {
      await leaveBalanceService.revoke(balanceId, { reason: reason || undefined });
      setSuccessMessage('休暇を取り消しました');
      setError(null);
      loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('休暇取り消しエラー:', err);
      setError(err.response?.data?.error || '休暇の取り消しに失敗しました');
      setSuccessMessage(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>;
  }

  const filteredAndSortedBalances = getFilteredAndSortedBalances();

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', color: '#ccc' }}>▼</span>;
    return <span style={{ marginLeft: '4px' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'clamp(10px, 3vw, 15px)', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>休暇残数管理</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleDownloadLedger}
              disabled={ledgerDownloading}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                backgroundColor: ledgerDownloading ? '#94A3B8' : '#2563EB',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: ledgerDownloading ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              {ledgerDownloading ? '出力中…' : '年次有給休暇管理簿 (CSV)'}
            </button>
            <button
              onClick={handleOpenGrantModal}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              + 休暇付与
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              ダッシュボードに戻る
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {/* フィルターセクション */}
      <div style={{
        padding: 'clamp(12px, 3vw, 20px)',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        marginBottom: 'clamp(10px, 3vw, 20px)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>絞り込み</h3>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
          {/* 部署選択 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <label htmlFor="department-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
              部署
            </label>
            <select
              id="department-filter"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)'
              }}
            >
              <option value="all">全部署</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id?.toString() || ''}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* 在職状態 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <EmploymentFilter id="leave-balances-employment" value={employment} onChange={setEmployment} count={users.length} layout="stacked" />
          </div>

          {/* 検索 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <label htmlFor="user-search" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
              検索（氏名・ログインID）
            </label>
            <input
              id="user-search"
              type="text"
              placeholder="検索..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)'
              }}
            />
          </div>

          {/* 休暇種別選択 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <label htmlFor="leave-type-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
              休暇種別
            </label>
            <select
              id="leave-type-filter"
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)'
              }}
            >
              <option value="all">全種別</option>
              {leaveTypes.map(lt => (
                <option key={lt.id} value={lt.id.toString()}>
                  {lt.name} ({lt.is_paid ? '有給' : '無給'})
                </option>
              ))}
            </select>
          </div>

          {/* 入社月選択 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <label htmlFor="hire-month-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
              入社月
            </label>
            <select
              id="hire-month-filter"
              value={selectedHireMonth}
              onChange={(e) => setSelectedHireMonth(e.target.value)}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)'
              }}
            >
              <option value="all">全期間</option>
              {hireMonths.map(month => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* クリアボタン */}
        <div style={{ textAlign: 'right' }}>
          <button
            onClick={() => {
              setSelectedDepartmentId('all');
              setUserSearchQuery('');
              setSelectedLeaveTypeId('all');
              setSelectedHireMonth('all');
            }}
            style={{
              padding: 'clamp(4px, 1vw, 6px) clamp(10px, 2vw, 12px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 2vw, 13px)',
              whiteSpace: 'nowrap'
            }}
          >
            クリア
          </button>
        </div>
      </div>

      {/* 空状態は**絞り込んだ後**の件数で判定する。絞り込み前の件数で見ると、
          「退職者」を選んで該当ゼロのときにヘッダだけの表が理由も無く残る。 */}
      {filteredAndSortedBalances.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          {balances.length === 0 ? (
            <>
              <p>休暇残数がありません</p>
              <p style={{ fontSize: '14px' }}>「休暇付与」ボタンから社員に休暇を付与してください</p>
            </>
          ) : (
            <>
              <p>絞り込みに一致する休暇残数がありません</p>
              <p style={{ fontSize: '14px' }}>「クリア」で絞り込みを解除できます</p>
            </>
          )}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
          <table style={{
            width: '100%',
            minWidth: '900px',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th
                  onClick={() => handleSort('user_name')}
                  style={{
                    padding: 'clamp(8px, 2vw, 12px)',
                    textAlign: 'left',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  従業員<SortIcon field="user_name" />
                </th>
                <th
                  onClick={() => handleSort('leave_type_name')}
                  style={{
                    padding: 'clamp(8px, 2vw, 12px)',
                    textAlign: 'left',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  休暇種別<SortIcon field="leave_type_name" />
                </th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  付与
                </th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  消費
                </th>
                <th
                  onClick={() => handleSort('balance')}
                  style={{
                    padding: 'clamp(8px, 2vw, 12px)',
                    textAlign: 'left',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  残数<SortIcon field="balance" />
                </th>
                <th
                  onClick={() => handleSort('valid_until')}
                  style={{
                    padding: 'clamp(8px, 2vw, 12px)',
                    textAlign: 'left',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  有効期限<SortIcon field="valid_until" />
                </th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedBalances.map((balance) => (
                <tr key={balance.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {balance.user_name}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {balance.leave_type_name}
                    {balance.is_paid && (
                      <span style={{
                        marginLeft: '6px',
                        padding: '2px 6px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: 'clamp(10px, 2vw, 11px)'
                      }}>
                        有給
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {balance.granted_days}日
                    {(balance.granted_remaining_hours ?? 0) > 0 && ` ${balance.granted_remaining_hours}時間`}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {balance.used_days}日
                    {(balance.used_remaining_hours ?? 0) > 0 && ` ${balance.used_remaining_hours}時間`}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)', fontWeight: 'bold' }}>
                    {balance.balance_days}日
                    {balance.balance_remaining_hours > 0 && ` ${balance.balance_remaining_hours}時間`}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)' }}>
                    {new Date(balance.valid_from).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                    <br />
                    〜 {new Date(balance.valid_until).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center' }}>
                    <button
                      onClick={() => handleRevoke(balance.id, balance.user_name || '', balance.leave_type_name || '')}
                      style={{
                        padding: 'clamp(4px, 1vw, 6px) clamp(10px, 2vw, 12px)',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: 'clamp(11px, 2vw, 13px)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      取消
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGrantModal && (
        <div
          onClick={handleCloseGrantModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: 'clamp(15px, 4vw, 30px)',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: 'clamp(16px, 4vw, 20px)' }}>休暇付与（複数選択可）</h2>
            <form onSubmit={handleGrantSubmit}>
              {/* 対象社員の絞り込み */}
              <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  対象社員の絞り込み
                </label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  {/* 部署選択 */}
                  <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                    <label htmlFor="grant-department-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                      部署
                    </label>
                    <select
                      id="grant-department-filter"
                      value={grantModalDepartmentId}
                      onChange={(e) => setGrantModalDepartmentId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 'clamp(6px, 1.5vw, 8px)',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: 'clamp(12px, 2.5vw, 14px)'
                      }}
                    >
                      <option value="all">全部署</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id?.toString() || ''}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 検索 */}
                  <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                    <label htmlFor="grant-user-search" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                      検索（氏名・ログインID）
                    </label>
                    <input
                      id="grant-user-search"
                      type="text"
                      placeholder="検索..."
                      value={grantModalSearchQuery}
                      onChange={(e) => setGrantModalSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 'clamp(6px, 1.5vw, 8px)',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: 'clamp(12px, 2.5vw, 14px)'
                      }}
                    />
                  </div>

                  {/* 入社年月 */}
                  <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                    <label htmlFor="grant-hire-month-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                      入社年月
                    </label>
                    <select
                      id="grant-hire-month-filter"
                      value={grantModalHireMonth}
                      onChange={(e) => setGrantModalHireMonth(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 'clamp(6px, 1.5vw, 8px)',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: 'clamp(12px, 2.5vw, 14px)'
                      }}
                    >
                      <option value="all">全期間</option>
                      {hireMonths.map(month => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 対象社員の選択 */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  対象社員 * （選択: {selectedUserIds.length}名）
                </label>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: 'white'
                }}>
                  {grantModalFilteredUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
                      該当する社員がいません
                    </div>
                  ) : (
                    <>
                      {/* 全選択/全解除 */}
                      <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #dee2e6' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.length === grantModalFilteredUsers.length && grantModalFilteredUsers.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds(grantModalFilteredUsers.map(u => u.id));
                              } else {
                                setSelectedUserIds([]);
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', fontWeight: 'bold' }}>
                            全て選択/解除
                          </span>
                        </label>
                      </div>
                      {/* ユーザーリスト */}
                      {grantModalFilteredUsers.map((u) => (
                        <div key={u.id} style={{ marginBottom: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(u.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUserIds([...selectedUserIds, u.id]);
                                } else {
                                  setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                                }
                              }}
                              style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                              {u.name} ({u.username})
                              {u.department_name && <span style={{ color: '#6c757d', fontSize: 'clamp(11px, 2vw, 12px)' }}> - {u.department_name}</span>}
                            </span>
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="leave_type_id" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  休暇種別 *
                </label>
                <select
                  id="leave_type_id"
                  value={grantFormData.leave_type_id}
                  onChange={(e) => setGrantFormData({ ...grantFormData, leave_type_id: parseInt(e.target.value) })}
                  required
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                >
                  <option value={0}>選択してください</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name} ({lt.is_paid ? '有給' : '無給'})
                    </option>
                  ))}
                </select>
                <p style={{ marginTop: '4px', fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>
                  ※残数管理が有効な休暇種別のみ表示されます
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label htmlFor="days" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                    付与日数 *
                  </label>
                  <input
                    type="number"
                    id="days"
                    value={grantFormData.days}
                    onChange={(e) => setGrantFormData({ ...grantFormData, days: parseInt(e.target.value) || 0 })}
                    min="0"
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="hours" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                    付与時間 *
                  </label>
                  <input
                    type="number"
                    id="hours"
                    value={grantFormData.hours}
                    onChange={(e) => setGrantFormData({ ...grantFormData, hours: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="23"
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label htmlFor="valid_from" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                    有効開始日 *
                  </label>
                  <input
                    type="date"
                    id="valid_from"
                    value={grantFormData.valid_from}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      if (startDate) {
                        // 2年後の前日を計算
                        const start = new Date(startDate);
                        const twoYearsLater = new Date(start);
                        twoYearsLater.setFullYear(start.getFullYear() + 2);
                        twoYearsLater.setDate(twoYearsLater.getDate() - 1);
                        const endDate = twoYearsLater.toISOString().split('T')[0];
                        setGrantFormData({ ...grantFormData, valid_from: startDate, valid_until: endDate });
                      } else {
                        setGrantFormData({ ...grantFormData, valid_from: startDate });
                      }
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="valid_until" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                    有効終了日 *
                  </label>
                  <input
                    type="date"
                    id="valid_until"
                    value={grantFormData.valid_until}
                    onChange={(e) => setGrantFormData({ ...grantFormData, valid_until: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="reason" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  付与理由
                </label>
                <textarea
                  id="reason"
                  value={grantFormData.reason}
                  onChange={(e) => setGrantFormData({ ...grantFormData, reason: e.target.value })}
                  placeholder="例: 2025年度分有給休暇"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* エラーメッセージ */}
              {error && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  fontSize: 'clamp(12px, 2.5vw, 14px)'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseGrantModal}
                  style={{
                    padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  style={{
                    padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  付与
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveBalancesPage;
